import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, ExternalLink } from "lucide-react";
import api from "../lib/api";

export default function Leaderboard() {
  const [data, setData] = useState({ items: [], total_reports: 0, avg_score: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/leaderboard?limit=10")
      .then((r) => setData(r.data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  const scoreColor = (s) =>
    s >= 80 ? "text-emerald-700" : s >= 60 ? "text-amber-700" : "text-[#D0021B]";

  return (
    <div className="min-h-screen bg-zinc-50 bg-grid">
      <header className="border-b border-zinc-950 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-3">
            <span className="font-mono text-[10px] tracking-[0.18em] text-zinc-400">SMARTERP</span>
            <span className="font-display text-2xl font-black tracking-tight">Enterprise.</span>
          </Link>
          <Link to="/cash-health-check" className="text-xs font-mono text-zinc-700 hover:text-[#FF3E00]" data-testid="lb-run-own-link">
            RUN YOUR OWN →
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 mb-2">/ LEADERBOARD</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Who&apos;s running<br />
          <span className="text-[#FF3E00]">the tightest ship.</span>
        </h1>
        <p className="mt-3 max-w-xl text-zinc-700">
          The 10 most-recent Cash Flow Health Check scores. Company names are partially redacted for privacy.
        </p>

        {/* stat row */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-0 border border-zinc-950 bg-white" data-testid="lb-stats">
          <div className="border-r border-zinc-200 p-5">
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">TOTAL REPORTS</div>
            <div className="font-display font-black text-3xl tnum mt-1" data-testid="lb-total">{data.total_reports}</div>
          </div>
          <div className="border-r border-zinc-200 p-5">
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">AVG SCORE</div>
            <div className="font-display font-black text-3xl tnum mt-1" data-testid="lb-avg">{data.avg_score}<span className="text-zinc-400 text-base"> / 100</span></div>
          </div>
          <div className="p-5 col-span-2 sm:col-span-1 flex items-center">
            <Trophy size={18} className="text-[#FF3E00] mr-2" />
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">UPDATED LIVE</span>
          </div>
        </div>

        {/* table */}
        <div className="mt-6 border border-zinc-950 bg-white animate-fade-up">
          <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase">
            recent_scores.csv
          </div>
          {loading ? (
            <div className="p-8 text-center font-mono text-xs text-zinc-400 pulse-dot">LOADING…</div>
          ) : data.items.length === 0 ? (
            <div className="p-8 text-center font-mono text-xs text-zinc-400">NO REPORTS YET — BE THE FIRST</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3 w-12">#</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-right px-4 py-3 w-32">Score</th>
                  <th className="text-right px-4 py-3 w-32">When</th>
                  <th className="text-right px-4 py-3 w-16"></th>
                </tr>
              </thead>
              <tbody data-testid="lb-rows">
                {data.items.map((row, idx) => (
                  <tr key={row.share_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`lb-row-${idx}`}>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400 tnum">{String(idx + 1).padStart(2, "0")}</td>
                    <td className="px-4 py-3 font-medium">{row.company}</td>
                    <td className={`px-4 py-3 text-right font-display font-black text-lg tnum ${scoreColor(row.score)}`}>
                      {row.score}<span className="text-zinc-400 text-xs font-normal"> /100</span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-zinc-500 tnum">
                      {row.created_at ? new Date(row.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/cash-health-check/r/${row.share_id}`} className="text-[#FF3E00] hover:underline inline-flex items-center gap-1 text-xs font-mono" data-testid={`lb-view-${row.share_id}`}>
                        VIEW <ExternalLink size={10} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="border-t border-zinc-950 bg-zinc-50 px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-zinc-600">Curious how your business stacks up?</div>
            <Link to="/cash-health-check" className="bg-[#FF3E00] text-white font-semibold text-xs px-5 py-2.5 hover:bg-zinc-950 transition-colors flex items-center gap-2" data-testid="lb-cta-btn">
              RUN YOUR OWN CHECK <ArrowRight size={12} />
            </Link>
          </div>
        </div>

        <footer className="mt-12 text-center text-xs text-zinc-500">
          SMARTERP / CASH FLOW HEALTH CHECK · <Link to="/login" className="hover:text-zinc-950">Sign in →</Link>
        </footer>
      </div>
    </div>
  );
}
